import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Trash2,
  Loader2,
  CheckCircle2,
  Clock,
  AlertCircle,
  Bell,
  Send,
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useTheme } from '../contexts/ThemeContext.tsx';
import api from '../services/api.ts';

interface SidebarTasksListProps {
  recordId?: number;
  contactId: string;
  contactPhone: string;
  clinicId: number;
}

interface Task {
  id: number;
  task_type: string;
  title: string;
  description?: string;
  scheduled_for: string;
  status: 'pending' | 'in_progress' | 'completed' | 'canceled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  message_type?: string;
  message_content?: string;
  created_by?: { name?: string };
}

type TaskKind = 'message' | 'scheduled_message';

const PRIORITY_TONE: Record<string, { dark: string; light: string }> = {
  urgent: { dark: 'bg-red-900/40 text-red-300', light: 'bg-red-50 text-red-600' },
  high: { dark: 'bg-orange-900/40 text-orange-300', light: 'bg-orange-50 text-orange-600' },
  medium: { dark: 'bg-yellow-900/40 text-yellow-300', light: 'bg-yellow-50 text-yellow-700' },
  low: { dark: 'bg-sminy-primary/15 text-sminy-primary', light: 'bg-sminy-primary/10 text-sminy-primary' },
};

const PRIORITY_LABEL: Record<string, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
};

const FILTERS: Array<{ key: 'pending' | 'completed' | 'all'; label: string }> = [
  { key: 'pending', label: 'Pendentes' },
  { key: 'completed', label: 'Concluídas' },
  { key: 'all', label: 'Todas' },
];

const SidebarTasksList: React.FC<SidebarTasksListProps> = ({ contactId, recordId }) => {
  const resource = recordId ? `by-id/${recordId}` : encodeURIComponent(contactId);
  const { isDark } = useTheme();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'pending' | 'completed' | 'all'>('pending');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    kind: 'message' as TaskKind,
    title: '',
    description: '',
    message_content: '',
    scheduled_for: format(new Date(Date.now() + 60 * 60 * 1000), "yyyy-MM-dd'T'HH:mm"),
    priority: 'medium' as Task['priority'],
  });

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/contacts/${resource}/tasks`);
      const data: Task[] = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.tasks)
        ? res.data.tasks
        : [];
      console.log('[SidebarTasksList] Tarefas carregadas:', data.length);
      setTasks(data);
    } catch (e) {
      console.error('[SidebarTasksList] Erro ao carregar tarefas:', e);
    } finally {
      setLoading(false);
    }
  }, [resource]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const filtered = tasks
    .filter((t) => {
      if (filter === 'pending') return t.status === 'pending' || t.status === 'in_progress';
      if (filter === 'completed') return t.status === 'completed';
      return true;
    })
    .sort((a, b) => new Date(a.scheduled_for).getTime() - new Date(b.scheduled_for).getTime());

  const handleCreate = async () => {
    if (!form.title.trim()) return;
    if (form.kind === 'scheduled_message' && !form.message_content.trim()) return;

    // Validação extra para mensagem agendada: data deve ser futura
    if (form.kind === 'scheduled_message') {
      const scheduled = new Date(form.scheduled_for);
      if (scheduled.getTime() <= Date.now()) {
        alert('A data/hora de envio precisa ser no futuro para mensagens agendadas.');
        return;
      }
    }

    setSaving(true);
    try {
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const payload: any = {
        task_type: form.kind,
        title: form.title,
        description: form.description || undefined,
        scheduled_for: form.scheduled_for,
        priority: form.priority,
        reminder_minutes: 15,
      };
      if (form.kind === 'scheduled_message') {
        payload.message_type = 'text';
        payload.message_content = form.message_content;
      }

      console.log('[SidebarTasksList] Criando tarefa', { kind: form.kind, payload });
      await api.post(`/api/contacts/${resource}/tasks`, payload, {
        headers: { 'X-Timezone': userTimeZone },
      });
      setForm({ ...form, title: '', description: '', message_content: '' });
      setShowForm(false);
      await fetchTasks();
    } catch (e: any) {
      console.error('[SidebarTasksList] Erro ao criar tarefa:', e?.response?.data || e);
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (id: number) => {
    try {
      await api.post(`/api/tasks/${id}/complete`);
      await fetchTasks();
    } catch (e) {
      console.error('[SidebarTasksList] Erro ao completar tarefa:', e);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Excluir esta tarefa?')) return;
    try {
      await api.delete(`/api/tasks/${id}`);
      await fetchTasks();
    } catch (e) {
      console.error('[SidebarTasksList] Erro ao excluir tarefa:', e);
    }
  };

  return (
    <div className="p-4 space-y-3">
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="w-full rounded-xl bg-sminy-primary hover:bg-sminy-primary/90 px-4 py-2.5 text-sm font-medium text-white transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nova tarefa
        </button>
      )}

      {showForm && (
        <div
          className={`rounded-2xl border p-3 space-y-2 ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}
        >
          <div className={`flex p-1 rounded-xl ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>
            <button
              type="button"
              onClick={() => setForm({ ...form, kind: 'message' })}
              className={`flex-1 py-1.5 px-2 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5 ${
                form.kind === 'message'
                  ? isDark
                    ? 'bg-gray-700 text-sminy-primary'
                    : 'bg-white text-sminy-primary shadow-sm'
                  : isDark
                  ? 'text-gray-400 hover:text-gray-200'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Bell className="w-3.5 h-3.5" />
              Lembrete
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, kind: 'scheduled_message' })}
              className={`flex-1 py-1.5 px-2 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5 ${
                form.kind === 'scheduled_message'
                  ? isDark
                    ? 'bg-gray-700 text-sminy-primary'
                    : 'bg-white text-sminy-primary shadow-sm'
                  : isDark
                  ? 'text-gray-400 hover:text-gray-200'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Send className="w-3.5 h-3.5" />
              Mensagem agendada
            </button>
          </div>
          {form.kind === 'scheduled_message' && (
            <p className={`text-[11px] flex items-start gap-1.5 px-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              <Send className="w-3 h-3 mt-0.5 flex-shrink-0 text-sminy-primary" />
              A mensagem será enviada automaticamente para o contato no horário escolhido, respeitando as janelas de operação da clínica.
            </p>
          )}

          <input
            type="text"
            placeholder={form.kind === 'scheduled_message' ? 'Título interno (não é enviado)' : 'Título da tarefa'}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            autoFocus
            className={`w-full p-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
              isDark
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
            }`}
          />
          {form.kind === 'scheduled_message' ? (
            <textarea
              placeholder="Conteúdo da mensagem que será enviada ao contato..."
              rows={4}
              value={form.message_content}
              onChange={(e) => setForm({ ...form, message_content: e.target.value })}
              className={`w-full p-2.5 border rounded-xl text-sm resize-none focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              }`}
            />
          ) : (
            <textarea
              placeholder="Descrição (opcional)"
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className={`w-full p-2.5 border rounded-xl text-sm resize-none focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              }`}
            />
          )}
          <div className="grid grid-cols-2 gap-2">
            <input
              type="datetime-local"
              value={form.scheduled_for}
              onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })}
              className={`p-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
            <select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value as Task['priority'] })}
              className={`p-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-sminy-primary transition-colors ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="low">Baixa</option>
              <option value="medium">Média</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              disabled={
                !form.title.trim() ||
                saving ||
                (form.kind === 'scheduled_message' && !form.message_content.trim())
              }
              className="flex-1 rounded-xl bg-sminy-primary hover:bg-sminy-primary/90 disabled:opacity-50 px-4 py-2 text-sm font-medium text-white transition-colors flex items-center justify-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {form.kind === 'scheduled_message' ? 'Agendar envio' : 'Criar'}
            </button>
            <button
              onClick={() => {
                setShowForm(false);
                setForm({ ...form, title: '', description: '', message_content: '' });
              }}
              className={`rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                isDark
                  ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      <div className={`flex p-1 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
        {FILTERS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              filter === key
                ? isDark
                  ? 'bg-[#111827] text-sminy-primary'
                  : 'bg-white text-sminy-primary shadow-sm'
                : isDark
                ? 'text-gray-400 hover:text-gray-200'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-4 border-sminy-primary/30 border-t-sminy-primary rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 px-4">
          <div
            className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-3 ${
              isDark ? 'bg-gray-700' : 'bg-gray-100'
            }`}
          >
            <CheckCircle2 className={`w-8 h-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          </div>
          <h3 className={`text-sm font-semibold mb-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
            {filter === 'pending'
              ? 'Sem tarefas pendentes'
              : filter === 'completed'
              ? 'Sem tarefas concluídas'
              : 'Sem tarefas'}
          </h3>
          <p className={`text-xs text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Crie uma tarefa para acompanhar este contato.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((task) => {
            const dueDate = parseISO(task.scheduled_for);
            const isOverdue =
              (task.status === 'pending' || task.status === 'in_progress') &&
              dueDate < new Date();
            const isCompleted = task.status === 'completed';
            const tone = PRIORITY_TONE[task.priority] || PRIORITY_TONE.medium;

            return (
              <div
                key={task.id}
                className={`rounded-2xl border p-3 transition-colors ${
                  isDark
                    ? 'bg-gray-800 border-gray-700 hover:border-gray-600'
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => !isCompleted && handleComplete(task.id)}
                    disabled={isCompleted}
                    title={isCompleted ? 'Concluída' : 'Marcar como concluída'}
                    className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      isCompleted
                        ? 'bg-sminy-primary border-sminy-primary'
                        : isDark
                        ? 'border-gray-600 hover:border-sminy-primary'
                        : 'border-gray-300 hover:border-sminy-primary'
                    }`}
                  >
                    {isCompleted && <CheckCircle2 className="w-3 h-3 text-white" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4
                        className={`text-sm font-semibold flex-1 ${
                          isCompleted ? 'line-through opacity-60' : ''
                        } ${isDark ? 'text-gray-200' : 'text-gray-900'}`}
                      >
                        {task.title}
                      </h4>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${
                          isDark ? tone.dark : tone.light
                        }`}
                      >
                        {PRIORITY_LABEL[task.priority] || task.priority}
                      </span>
                    </div>
                    {task.task_type === 'scheduled_message' && (
                      <div
                        className={`mb-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          isDark ? 'bg-sminy-primary/15 text-sminy-primary' : 'bg-sminy-primary/10 text-sminy-primary'
                        }`}
                      >
                        <Send className="w-2.5 h-2.5" />
                        Mensagem agendada
                      </div>
                    )}
                    {task.task_type === 'scheduled_message' && task.message_content ? (
                      <p
                        className={`text-xs mb-1 italic px-2 py-1.5 rounded-lg border-l-2 border-sminy-primary ${
                          isDark ? 'bg-gray-900/50 text-gray-300' : 'bg-gray-50 text-gray-700'
                        }`}
                      >
                        “{task.message_content}”
                      </p>
                    ) : task.description ? (
                      <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {task.description}
                      </p>
                    ) : null}
                    <div
                      className={`flex items-center gap-1.5 text-xs ${
                        isOverdue
                          ? isDark
                            ? 'text-red-300'
                            : 'text-red-600'
                          : isDark
                          ? 'text-gray-500'
                          : 'text-gray-500'
                      }`}
                    >
                      {isOverdue ? (
                        <AlertCircle className="w-3 h-3" />
                      ) : (
                        <Clock className="w-3 h-3" />
                      )}
                      <span title={format(dueDate, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}>
                        {formatDistanceToNow(dueDate, { locale: ptBR, addSuffix: true })}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(task.id)}
                    title="Excluir"
                    className={`flex-shrink-0 p-1.5 rounded-lg transition-colors ${
                      isDark ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500 hover:bg-red-50'
                    }`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SidebarTasksList;
